import React, { useState, useEffect, useCallback } from 'react';
import { Search, Edit, Eye, Filter, Calendar, DollarSign, User, Building2, Receipt, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { Donation } from '../../lib/sdk/index';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { useAuthStore } from '../../stores/authStore';
import { useDonations, useDonationTypes, useOffices, useUpdateDonation, useDeleteDonation } from '../../hooks/useQueries';
import { donationToReceiptPayload, printDonationReceipt } from '../../lib/printReceipt';
import { getErrorMessage } from '../../lib/utils/errorMessage';

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

interface DonationType {
  _id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

interface Office {
  _id: string;
  name: string;
  officeNo: number;
}

const ManageDonations: React.FC = () => {
  const { user } = useAuthStore();
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [donorType, setDonorType] = useState('');
  const [officeId, setOfficeId] = useState('');
  const [donationTypeId, setDonationTypeId] = useState('');
  
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const itemsPerPage = 50;
  
  // Search params for React Query
  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: itemsPerPage,
    search: '',
    donorType: '',
    officeId: '',
    donationTypeId: ''
  });
  
  // Use React Query hooks
  const { data: donationsResponse, isLoading: donationsLoading, refetch: refetchDonations } = useDonations(searchParams);
  const { data: donationTypesData = [] } = useDonationTypes();
  const { data: officesResponse = [] } = useOffices();
  
  // Extract data from responses
  const donations = donationsResponse?.data || (Array.isArray(donationsResponse) ? donationsResponse : []);
  const donationTypes = Array.isArray(donationTypesData) ? donationTypesData.filter((dt: any) => dt.isActive) : [];
  const offices = Array.isArray(officesResponse?.data) ? officesResponse.data : (Array.isArray(officesResponse) ? officesResponse : []);
  const totalPages = donationsResponse?.pagination?.totalPages || 1;
  const totalItems = donationsResponse?.pagination?.totalItems || donations.length;
  
  const initialLoading = donationsLoading;
  const tableLoading = donationsLoading;
  
  const [viewingDonation, setViewingDonation] = useState<Donation | null>(null);
  
  // Edit modal state
  const [editingDonation, setEditingDonation] = useState<Donation | null>(null);
  const [editFormData, setEditFormData] = useState({
    ref: '',
    amount: '',
    donationTypeId: ''
  });

  // Update search params when filters change
  useEffect(() => {
    setSearchParams({
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearchTerm,
      donorType: donorType || undefined,
      officeId: officeId || undefined,
      donationTypeId: donationTypeId || undefined
    });
  }, [currentPage, debouncedSearchTerm, donorType, officeId, donationTypeId, itemsPerPage]);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Mutations
  const updateDonationMutation = useUpdateDonation({
    onSuccess: async (updated: Donation) => {
      toast.success('Donation updated successfully');
      setEditingDonation(null);
      setEditFormData({ ref: '', amount: '', donationTypeId: '' });
      refetchDonations();
      // Best-effort reprint — never roll back an edit if the printer fails
      await printDonationReceipt(donationToReceiptPayload(updated), { mandatory: false });
    },
    onError: (error: any) => {
      console.error('Error updating donation:', error);
      toast.error(getErrorMessage(error, 'Failed to update donation'));
    }
  });

  const deleteDonationMutation = useDeleteDonation({
    onSuccess: () => {
      toast.success('Donation deleted successfully');
      refetchDonations();
    },
    onError: (error: any) => {
      console.error('Error deleting donation:', error);
      toast.error(getErrorMessage(error, 'Failed to delete donation'));
    }
  });




  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // No need to call loadDonations() here as useEffect will handle it
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDonorType('');
    setOfficeId('');
    setDonationTypeId('');
    setCurrentPage(1);
    // No need to call loadDonations() here as useEffect will handle it
  };

  const canEditDonation = (donation: Donation) => {
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
    const donationDate = new Date(donation.createdAt);
    
    // Check if user is super admin
    const isSuperAdmin = user?.role === 'SuperAdmin';
    
    return donationDate > threeDaysAgo || isSuperAdmin;
  };

  const openEditModal = (donation: Donation) => {
    if (!canEditDonation(donation)) {
      toast.error('Cannot edit donations older than 3 days');
      return;
    }
    
    setEditingDonation(donation);
    setEditFormData({
      ref: donation.ref || '',
      amount: donation.amount.toString(),
      donationTypeId: donation.donationTypeId._id
    });
  };

  const closeEditModal = () => {
    setEditingDonation(null);
    setEditFormData({ ref: '', amount: '', donationTypeId: '' });
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingDonation) return;
    
    const updateData = {
      ref: editFormData.ref || undefined,
      amount: parseFloat(editFormData.amount),
      donationTypeId: editFormData.donationTypeId,
      donationTypeName: donationTypes.find(t => t._id === editFormData.donationTypeId)?.name || ''
    };
    
    updateDonationMutation.mutate({
      id: editingDonation._id,
      data: updateData
    });
  };

  const viewDonationDetails = (donation: Donation) => {
    setViewingDonation(donation);
  };

  const closeDonationDetails = () => {
    setViewingDonation(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount: number) => {
    return `Rs. ${amount.toLocaleString('en-PK')}`;
  };

  if (initialLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Manage Donations</h1>
        <p className="text-gray-600 dark:text-gray-400">View and manage donation records</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <DollarSign className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Donations</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{totalItems}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <Calendar className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Current Page</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{currentPage}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <User className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Unique Donors</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {new Set(donations.map(d => d.donorId)).size}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <Building2 className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Offices</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {new Set(donations.map(d => (d.donorOfficeId as any)?._id || d.donorOfficeId?.toString() || d.donorOfficeId)).size}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="form-label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by donor name, ID, or ref..."
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="form-label">Donor Type</label>
              <select
                value={donorType}
                onChange={(e) => setDonorType(e.target.value)}
                className="form-select"
              >
                <option value="">All Types</option>
                <option value="donor">Regular Donor</option>
                <option value="guestDonor">Guest Donor</option>
              </select>
            </div>

            <div>
              <label className="form-label">Office</label>
              <select
                value={officeId}
                onChange={(e) => setOfficeId(e.target.value)}
                className="form-select"
              >
                <option value="">All Offices</option>
                {offices.map(office => (
                  <option key={office._id} value={office._id}>
                    {office.name} ({office.officeNo})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Donation Type</label>
              <select
                value={donationTypeId}
                onChange={(e) => setDonationTypeId(e.target.value)}
                className="form-select"
              >
                <option value="">All Types</option>
                {donationTypes.map(type => (
                  <option key={type._id} value={type._id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex space-x-2">
            <Button type="submit" variant="primary" leftIcon={<Filter className="h-4 w-4" />}>
              Apply Filters
            </Button>
            <Button type="button" variant="secondary" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </form>
      </div>

      {/* Donations Table */}
      <div className="card">
        {tableLoading ? (
          <div className="text-center py-12">
            <LoadingSpinner />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Searching donations...</p>
          </div>
        ) : donations.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No donations found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || donorType || officeId || donationTypeId
                ? 'Try adjusting your search criteria.'
                : 'Get started by creating your first donation.'
              }
            </p>
          </div>
        ) : (
          <TableRoot>
            <Table>
              <THead>
                <TR>
                  <TH>Receipt ID</TH>
                  <TH>Donor</TH>
                  <TH>Type</TH>
                  <TH>Amount</TH>
                  <TH>Office</TH>
                  <TH>Cashier</TH>
                  <TH>Created</TH>
                  <TH>Actions</TH>
                </TR>
              </THead>
              <TBody>
                {donations.map((donation) => (
                  <TR key={donation._id}>
                    <TD className="font-medium">#{donation.receiptId}</TD>
                    <TD>
                      <div>
                        <div className="font-medium">{donation.donorName}</div>
                        <div className="text-sm text-gray-500">{donation.donorId}</div>
                      </div>
                    </TD>
                    <TD>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        donation.donorType === 'donor'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      }`}>
                        {donation.donorType === 'donor' ? 'Regular' : 'Guest'}
                      </span>
                    </TD>
                    <TD className="font-medium">{formatAmount(donation.amount)}</TD>
                    <TD>
                      <div>
                        <div className="font-medium">{donation.donorOfficeName}</div>
                        <div className="text-sm text-gray-500">#{donation.donorOfficeNo}</div>
                      </div>
                    </TD>
                    <TD>
                      <div>
                        <div className="font-medium">{donation.cashierName}</div>
                        <div className="text-sm text-gray-500">{donation.cashierRole}</div>
                      </div>
                    </TD>
                    <TD className="text-sm text-gray-500">{formatDate(donation.createdAt)}</TD>
                    <TD>
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(donation)}
                          disabled={!canEditDonation(donation)}
                          leftIcon={<Edit className="h-4 w-4" />}
                          title={canEditDonation(donation) ? 'Edit donation' : 'Cannot edit donations older than 3 days'}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewDonationDetails(donation)}
                          leftIcon={<Eye className="h-4 w-4" />}
                          title="View donation details"
                        >
                          View
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableRoot>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
            </div>
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="secondary"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingDonation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Edit Donation #{editingDonation.receiptId}
            </h3>
            
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="form-label">Reference (Optional)</label>
                <Input
                  type="text"
                  value={editFormData.ref}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, ref: e.target.value }))}
                />
              </div>

              <div>
                <label className="form-label">Amount *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">Rs.</span>
                  <Input
                    type="number"
                    value={editFormData.amount}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, amount: e.target.value }))}
                    min="0"
                    step="1"
                    required
                    className="pl-12"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Donation Type *</label>
                <select
                  value={editFormData.donationTypeId}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, donationTypeId: e.target.value }))}
                  required
                  className="form-select"
                >
                  {donationTypes.map(type => (
                    <option key={type._id} value={type._id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex space-x-2 pt-4">
                <Button type="submit" variant="primary" className="flex-1">
                  Update Donation
                </Button>
                <Button type="button" variant="secondary" onClick={closeEditModal} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageDonations;
